import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UpdateMarinaSettingsInput } from '@/lib/marina/validators'

export interface MarinaSettings {
  id: string
  name: string
  address: string | null
  city: string | null
  country: string
  timezone: string
  currency: string
  defaultTaxRate: string
  logoUrl: string | null
  supportEmail: string | null
  supportPhone: string | null
  updatedAt: string
}

export interface AuditLogEntry {
  id: string
  userId: string | null
  action: string
  detail: string | null
  ip: string | null
  createdAt: string
  user?: { email: string; name: string } | null
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) throw new Error((await res.json().catch(() => ({ error: 'err' }))).error || `HTTP ${res.status}`)
  return res.json()
}

export function useMarinaSettings() {
  return useQuery({
    queryKey: ['settings', 'marina'],
    queryFn: () => apiFetch<{ settings: MarinaSettings }>('/api/settings/marina'),
  })
}

export function useUpdateMarinaSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateMarinaSettingsInput) =>
      apiFetch<{ settings: MarinaSettings }>('/api/settings/marina', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'marina'] }),
  })
}

export function useAuditLogs(actionFilter?: string) {
  const qs = new URLSearchParams()
  if (actionFilter) qs.set('action', actionFilter)
  return useQuery({
    queryKey: ['audit-logs', actionFilter],
    queryFn: () => apiFetch<{ items: AuditLogEntry[] }>(`/api/admin/audit-logs${qs.toString() ? `?${qs}` : ''}`),
    staleTime: 10_000,
  })
}
